import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, finalize, map, shareReplay, tap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { User } from '@auth0/auth0-angular';
import { ENV } from '@bodyanalytics/app-config';
import { EmbeddedSessionTokensI } from '@bodyanalytics/data-models-ui';
import { ApiLocalRoutesEnum } from '@bodyanalytics/ui-core';

export interface EmbeddedSessionI {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
  /** Epoch ms after which the access token is considered dead. */
  expiresAt: number;
  user: User;
}

const STORAGE_KEY = 'abx.embedded-session';
/** Refresh/expiry safety margin — treat the token as expired 30s early. */
const EXPIRY_SKEW_MS = 30_000;

/**
 * Client half of the embedded ROPG login (login redesign, flag
 * `embeddedLoginEnabled`). Exchanges credentials via the backend
 * `auth-session-api` proxy and holds the resulting session for the tab.
 *
 * Storage trade-off (ADR-0033): the ACCESS token (short-lived) and display
 * claims persist in sessionStorage so a reload keeps the session until token
 * expiry; the REFRESH token is memory-only and never persisted (OWASP A02 —
 * a storage dump must not yield a long-lived credential). Closing the tab
 * drops everything. XSS remains the attack that matters; CSP/Helmet are the
 * controls for it.
 */
@Injectable({ providedIn: 'root' })
export class EmbeddedSessionService {
  private appConfig = inject(ENV);
  private httpClient = inject(HttpClient);

  private readonly sessionSubject$ = new BehaviorSubject<EmbeddedSessionI | null>(this.restore());

  /** The current session, or null. Emits on login/refresh/clear. */
  readonly session$ = this.sessionSubject$.asObservable();
  readonly isActive$ = this.session$.pipe(map(() => this.hasValidSession()));
  readonly user$: Observable<User | null> = this.session$.pipe(
    map(session => (session && this.hasValidSession() ? session.user : null)),
  );

  get isEnabled(): boolean {
    return this.appConfig.embeddedLoginEnabled === true;
  }

  hasValidSession(): boolean {
    const session = this.sessionSubject$.value;
    return !!session && Date.now() < session.expiresAt - EXPIRY_SKEW_MS;
  }

  get accessToken(): string | null {
    return this.hasValidSession() ? (this.sessionSubject$.value as EmbeddedSessionI).accessToken : null;
  }

  get currentUser(): User | null {
    return this.hasValidSession() ? (this.sessionSubject$.value as EmbeddedSessionI).user : null;
  }

  login(emailAddress: string, password: string): Observable<EmbeddedSessionI> {
    return this.httpClient
      .post<EmbeddedSessionTokensI>(`${this.appConfig.api}/${ApiLocalRoutesEnum.API_SUFFIX_AUTH_SESSION_LOGIN}`, {
        emailAddress,
        password,
      })
      .pipe(
        map(tokens => this.toSession(tokens)),
        tap(session => this.store(session)),
      );
  }

  /** True when an expired session still holds a refresh token worth trying. */
  get canRefresh(): boolean {
    return !!this.sessionSubject$.value?.refreshToken;
  }

  private refreshInFlight$: Observable<EmbeddedSessionI> | null = null;

  /**
   * Exchange the stored refresh token for a fresh session. Concurrent callers
   * (e.g. several intercepted requests hitting an expired session at once)
   * share one in-flight exchange. A failed refresh clears the session — the
   * token was single-use/revoked, so keeping it would just loop.
   */
  refresh(): Observable<EmbeddedSessionI> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }
    const refreshToken = this.sessionSubject$.value?.refreshToken;
    this.refreshInFlight$ = this.httpClient
      .post<EmbeddedSessionTokensI>(`${this.appConfig.api}/${ApiLocalRoutesEnum.API_SUFFIX_AUTH_SESSION_REFRESH}`, {
        refreshToken,
      })
      .pipe(
        map(tokens => this.toSession(tokens)),
        tap(session => this.store(session)),
        catchError(error => {
          this.clear();
          return throwError(() => error);
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    return this.refreshInFlight$;
  }

  clear(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage unavailable (private mode / disabled) — in-memory state still clears.
    }
    this.sessionSubject$.next(null);
  }

  private toSession(tokens: EmbeddedSessionTokensI): EmbeddedSessionI {
    return {
      accessToken: tokens.accessToken,
      idToken: tokens.idToken,
      refreshToken: tokens.refreshToken,
      expiresAt: Date.now() + tokens.expiresIn * 1000,
      user: decodeIdTokenClaims(tokens.idToken),
    };
  }

  private store(session: EmbeddedSessionI): void {
    try {
      // OWASP A02 hardening: the refresh token NEVER touches sessionStorage —
      // it lives only in this service instance's memory. A reload keeps the
      // (short-lived) access token; silent refresh works within the tab's JS
      // lifetime only. XSS can still act as the page, but a storage dump
      // yields no long-lived credential.
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...session, refreshToken: undefined }));
    } catch {
      // Storage unavailable — the session still lives for this document.
    }
    this.sessionSubject$.next(session);
  }

  private restore(): EmbeddedSessionI | null {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const session = JSON.parse(raw) as EmbeddedSessionI;
      // Restored sessions never carry a refresh token (see store()), so an
      // expired one is simply dead — drop it.
      if (!session.accessToken || !session.expiresAt || Date.now() >= session.expiresAt - EXPIRY_SKEW_MS) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return session;
    } catch {
      return null;
    }
  }
}

/**
 * Decode the id_token's payload for display claims (email, name…). No client-side
 * signature verification — the token came over TLS from our own API, and nothing
 * security-relevant hangs off these claims client-side (the API re-validates the
 * access token's signature on every request).
 */
export function decodeIdTokenClaims(idToken: string): User {
  try {
    const payload = idToken.split('.')[1];
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(normalized), character => character.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as User;
  } catch {
    return {} as User;
  }
}
